import React, { useState } from "react";
import { Popover } from "@mantine/core";
import { IconCalendar } from "@tabler/icons-react";
import FilterChip, { FilterChipVariant } from "../FilterChip";
import { DateRangeMenuContent } from "../../reports/DateRangeFilter";
import { dateRangeLabel } from "../../../utils/dateRangeFormat";
import { DateRangePreset } from "../../../types/reports";

export interface DateRangeChipProps {
  preset: DateRangePreset;
  /** "YYYY-MM-DD" strings, kept as strings end-to-end (no timezone shift). */
  customStart: string | null;
  customEnd: string | null;
  onChange: (
    preset: DateRangePreset,
    customStart: string | null,
    customEnd: string | null,
  ) => void;
  /** Text label before the value (e.g. "Date"). When omitted, a calendar
   *  icon leads the chip instead. */
  label?: string;
  /** Muted trailing text, e.g. the resolved dates of the preset. */
  hint?: string;
  /** Tinted "applied" state (the "outline" look; the Sales token always
   *  reads as applied). */
  active?: boolean;
  /** Chip look; see FilterChip. Defaults to the Sales "token". */
  variant?: FilterChipVariant;
  /** Accessible name. Defaults to "Change date range". */
  ariaLabel?: string;
  /** Limit the menu to these presets (custom range is always offered). */
  presets?: ReadonlyArray<DateRangePreset>;
  /** Called when the menu opens or closes (e.g. to drop a half-picked
   *  custom range on close). */
  onOpenChange?: (opened: boolean) => void;
}

/**
 * Date-range filter chip. Opens the shared reports date-range menu (presets +
 * custom range) — the same `DateRangeMenuContent` the legacy reports toolbar
 * uses — so every date filter in the app offers identical options.
 */
export default function DateRangeChip({
  preset,
  customStart,
  customEnd,
  onChange,
  label,
  hint,
  active = false,
  variant = "token",
  ariaLabel = "Change date range",
  presets,
  onOpenChange,
}: DateRangeChipProps) {
  const [opened, setOpenedState] = useState(false);
  const setOpened = (next: boolean) => {
    setOpenedState(next);
    onOpenChange?.(next);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      withinPortal
      shadow="md"
      radius="var(--radius-lg)"
      closeOnClickOutside
    >
      <Popover.Target>
        <FilterChip
          variant={variant}
          label={label}
          icon={label ? undefined : <IconCalendar size={15} aria-hidden />}
          value={dateRangeLabel(preset, customStart, customEnd)}
          hint={hint}
          active={active || opened}
          aria-label={ariaLabel}
          onClick={() => setOpened(!opened)}
        />
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <DateRangeMenuContent
          preset={preset}
          customStart={customStart}
          customEnd={customEnd}
          onChange={onChange}
          onClose={() => setOpened(false)}
          presets={presets}
        />
      </Popover.Dropdown>
    </Popover>
  );
}
